import { useEffect, useState } from "react";
import { Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader, PriorityBadge } from "@/components/common/ui";

export default function Incidents() {
  const [incidents, setIncidents] = useState(null);
  const [residents, setResidents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "Behavioral Event", resident_id: "", severity: "Medium", summary: "" });

  const load = () => api.get("/incidents").then((r) => setIncidents(r.data)).catch(() => setIncidents([]));
  useEffect(() => { load(); api.get("/residents").then((r) => setResidents(r.data)).catch(() => {}); }, []);

  const submit = async () => {
    if (!form.resident_id || !form.summary) { toast.error("Resident and summary are required"); return; }
    try { await api.post("/incidents", form); toast.success("Incident reported"); setOpen(false);
      setForm({ type: "Behavioral Event", resident_id: "", severity: "Medium", summary: "" }); load(); }
    catch (e) { toast.error("Failed to create incident"); }
  };

  if (!incidents) return <div className="flex h-64 items-center justify-center text-muted-foreground"><Loader2 className="animate-spin" /></div>;

  const STATUS = { open: "#F04438", "under-review": "#F79009", closed: "#12B76A" };

  return (
    <div>
      <PageHeader overline="Care · Safety" title="Incidents"
        description="Structured incident reporting with review workflow and cross-module triggers to compliance and training."
        actions={
          <button data-testid="new-incident-btn" onClick={() => setOpen(true)}
            className="flex items-center gap-2 rounded-md bg-[#155EEF] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#0F4FD1]">
            <Plus size={16} /> Report Incident
          </button>
        } />

      <div className="space-y-3">
        {incidents.map((inc) => (
          <div key={inc.id} data-testid={`incident-${inc.id}`} className="rounded-xl border bg-card p-4 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-3">
                <span className="font-semibold">{inc.type}</span>
                <PriorityBadge level={inc.severity} />
                <span className="rounded-full px-2 py-0.5 text-xs font-semibold capitalize" style={{ color: STATUS[inc.status], backgroundColor: STATUS[inc.status] + "1a" }}>{inc.status}</span>
              </div>
              <span className="text-xs text-muted-foreground">{String(inc.occurred_at).slice(0, 16).replace("T", " ")}</span>
            </div>
            <div className="mt-1 text-sm text-muted-foreground">{inc.resident_name} · {inc.program_name} · reported by {inc.reported_by}</div>
            <p className="mt-2 text-sm">{inc.summary}</p>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-lg rounded-xl border bg-card p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold">Report Incident</h3>
              <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground"><X size={20} /></button>
            </div>
            <div className="space-y-3">
              <Field label="Type">
                <select data-testid="incident-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="input">
                  {["Behavioral Event", "Fall", "Medication Error", "Injury", "Elopement Attempt", "Property Damage"].map((t) => <option key={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Resident">
                <select data-testid="incident-resident" value={form.resident_id} onChange={(e) => setForm({ ...form, resident_id: e.target.value })} className="input">
                  <option value="">Select resident…</option>
                  {residents.map((r) => <option key={r.id} value={r.id}>{r.first_name} {r.last_name}</option>)}
                </select>
              </Field>
              <Field label="Severity">
                <select data-testid="incident-severity" value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} className="input">
                  {["Low", "Medium", "High"].map((s) => <option key={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Summary">
                <textarea data-testid="incident-summary" rows={3} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })}
                  placeholder="Describe what happened…" className="input resize-none" />
              </Field>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="rounded-md border px-4 py-2 text-sm font-medium">Cancel</button>
              <button data-testid="submit-incident-btn" onClick={submit} className="rounded-md bg-[#155EEF] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0F4FD1]">Submit Report</button>
            </div>
          </div>
        </div>
      )}
      <style>{`.input{height:2.5rem;width:100%;border-radius:0.375rem;border:1px solid hsl(var(--border));background:hsl(var(--card));padding:0 0.75rem;font-size:0.875rem;outline:none}.input:focus{border-color:#155EEF}textarea.input{height:auto;padding:0.5rem 0.75rem}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</label>{children}</div>;
}
